import { Users, Layers, Zap, Send, RefreshCcw, BarChart3, Mail, Cpu } from "lucide-react";
import { FiSend, FiRefreshCw, FiMail, FiBarChart2, FiLayers, FiSliders } from "react-icons/fi";
import { LuSettings, LuUsers, LuSmartphone, LuShieldCheck, LuPercent, LuCircleCheck, LuShield, LuClipboardList, LuCode, LuRocket } from "react-icons/lu";

// 1. Hero Data Block
export const EmailAppHeroData = {
  backgroundImage: "/images/serviceBanners/EmailAutomation.webp",
  whiteImage: "/images/serviceBanners/EmailAutomationW.webp",
  subTitle: "Email Marketing & Automation Services",
  title: (
    <>
      Scale Revenue with <br /> Smarter <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Email Automation</span>
    </>
  ),
  description:
    "We build intelligent email automation systems with behavioral targeting, automated workflows, and conversion focused strategies to improve engagement, nurture leads, and drive consistent revenue growth for businesses.",
  primaryButton: {
    title: "Get a Free Audit",
    onClick: () => console.log("Email Audit clicked"),
  },
  secondaryButton: {
    title: "View Case Studies",
    onClick: () => console.log("Email Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Email Deliverability",
      desc: "Optimized email authentication for stronger inbox placement.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Smart Automation",
      desc: "Behavior-based email workflows designed around user actions.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Customer Retention",
      desc: "Automated campaigns focused on re-engagement and repeat conversions.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "AI Optimization",
      desc: "Smarter subject lines and send-time strategies for better performance.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp", // Generic backend/automation icon
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(168,85,247,0.35)",
      iconGlow: "rgba(168,85,247,0.9)",
    },
  ],
};

// 2. Stats Block
export const emailStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

export const emailSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE AUTOMATE",
  buildTitle: "Automated Email Systems That Drive Consistent Revenue",
  buildCards: [
    {
      title: "Email Strategy",
      desc: "Content-driven email campaigns designed to build trust and engagement.",
      icon: FiSend,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Retention Automation",
      desc: "Re-engagement workflows focused on improving customer lifetime value.",
      icon: FiRefreshCw,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Behavior-Based Emails",
      desc: "Automated messaging triggered by real customer actions.",
      icon: FiMail,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Performance Analytics",
      desc: "Track opens, clicks, and campaign performance with clear insights.",
      icon: FiBarChart2,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Drip Campaign Setup",
      desc: "Automated email sequences for nurturing leads over time effectively.",
      icon: FiLayers,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "CRM Integration Workflows",
      desc: "Connect email automation with CRM systems for better deep personalization.",
      icon: FiSliders,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR EMAIL AUTOMATION GROWTH PROCESS",
  processTitle: "Our Email Automation Growth Process",
  processSteps: [
    {
      step: "01",
      title: "Audit & Setup",
      description: "Improve email deliverability and audience readiness.",
      icon: LuUsers,
    },
    {
      step: "02",
      title: "Strategy Planning",
      description: "Design automation flows and personalized email journeys.",
      icon: LuClipboardList,
    },
    {
      step: "03",
      title: "Campaign Creation",
      description: "Build automated email sequences based on user behavior.",
      icon: LuCode,
    },
    {
      step: "04",
      title: "Performance Optimization",
      description: "Continuously improve campaigns through testing and insights.",
      icon: LuRocket,
    },
    {
      step: "05",
      title: "A/B Testing Optimization",
      description: "Test subject lines and content variations for higher target engagement metrics.",
      icon: LuCircleCheck, // Use compile-safe icon child element for seamless bundle execution
    },
    {
      step: "06",
      title: "Segmentation Strategy",
      description: "Divide audiences for more personalized and effective email targeting flows.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY BUSINESSES CHOOSE EMAIL AUTOMATION",
  benefitsTitle: "Built for Customer Retention & Better ROI",
  benefits: [
    "Secure email authentication for stronger deliverability",
    "Smart audience segmentation and CRM-based automation",
    "Personalized email experiences based on customer behavior",
    "AI-driven testing for better email performance",
    "Automation strategies focused on long-term customer value",
  ],
  logoImage: "/images/serviceBanners/EmailAutomation.webp",
  whiteLogoImage: "/images/serviceBanners/EmailAutomationW.webp",
};

// 4. Portfolio / Results Block
export const emailPortfolioData = {
  subtitle: "OUR RESULTS",
  title: "Email Pipelines Built for Impact",
  buttonText: "View Case Studies",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "E-Com Giant",
      badge: "Retention Loop",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Re-activating 30% of 'lost' customers through behavioral win-back automation.",
      downloads: "30% Reactivated",
      rating: "5.0",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?q=80&w=2070&auto=format&fit=crop",
    },
    {
      title: "SaaS Launch",
      badge: "Nurture Sequence",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "Scaling demo bookings by 200% with intelligent logic-based lead nurturing.",
      downloads: "+200% Bookings",
      rating: "4.9",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2070&auto=format&fit=crop",
    },
  ],
};

// 5. Technologies Data
export const emailTechnologiesData = {
  techTitle: "OUR AUTOMATION TOOL STACK",
  technologies: [
    { name: "Klaviyo/HubSpot", icon: "Firebase" },
    { name: "Automation API", icon: "RestAPI" },
    { name: "Logic Engines", icon: "Provider" },
    { name: "Python/AI Tools", icon: "Dart" },
    { name: "Optimization", icon: "Vite" },
  ],
  trustTitle: "INTEGRATED WITH MAJOR CRMS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

// 6. About Block
export const emailAboutData = [
  {
    title: "Email Automation Services Built for Consistent Business Growth",
    content: (
      <>
        Businesses often lose potential revenue when communication stops after the first interaction. Strong{" "}
        <b className="text-text-primary font-semibold">email automation services</b> help brands stay connected with customers through personalized messaging that supports
        engagement and repeat conversions. Modern email systems are designed to nurture leads, improve retention, and create meaningful customer experiences over time. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, email workflows are planned around user behavior and business goals to help companies build stronger
        customer relationships while improving long-term revenue opportunities.
      </>
    ),
  },
  {
    title: "Email Marketing Automation That Improves Customer Engagement",
    content: (
      <>
        Effective <b className="text-text-primary font-semibold">email marketing automation</b> allows businesses to communicate with the right audience at the right moment.
        Instead of relying on manual outreach, brands use automated systems to guide customers through awareness, consideration, and retention stages. Businesses investing in{" "}
        <b className="text-text-primary font-semibold">automated email marketing services</b> often experience improved engagement because communication becomes more relevant and
        timely. Tycho Technologies focuses on practical automation strategies that support measurable growth while keeping messaging personalized and customer-focused.
      </>
    ),
  },
  {
    title: "CRM Email Automation Solutions for Better Retention",
    content: (
      <>
        Customer retention becomes easier when communication is connected to customer activity and buying behavior. Reliable{" "}
        <b className="text-text-primary font-semibold">crm email automation solutions</b> help businesses organize customer journeys, improve follow-ups, and maintain stronger
        engagement across different touchpoints. Businesses looking to strengthen digital relationships often align automation strategies with{" "}
        <a href="https://tychotechnologies.com/web3-community-building-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          web3 community building
        </a>{" "}
        to improve customer trust and long-term audience loyalty through meaningful engagement.
      </>
    ),
  },
  {
    title: "Why Businesses Choose an Email Automation Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">email automation company in india</b> continues to grow as brands seek scalable communication
        systems that improve business efficiency. Companies investing in <b className="text-text-primary font-semibold">email marketing services india</b> often want stronger
        customer retention, better lead nurturing, and measurable campaign performance. Businesses working with Tycho Technologies benefit from customized automation strategies
        designed around practical outcomes. Many organizations also combine email systems with{" "}
        <a href="https://tychotechnologies.com/social-media-marketing-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          social media marketing services
        </a>{" "}
        to create a more connected and growth-focused digital experience.
      </>
    ),
  },
];

// 7. FAQs
export const emailFaqData = [
  {
    q: "How do you ensure emails don't end up in spam?",
    a: "We implement rigorous technical authentication (SPF, DKIM, DMARC) and focus on high-quality list hygiene to maintain a strong sender reputation.",
  },
  {
    q: "Can you work with our existing CRM?",
    a: "Yes, we have experience integrating automation workflows with major platforms like HubSpot, Mailchimp, Klaviyo, and Salesforce.",
  },
];
